import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import Header from '../components/Headers/Header';
import CustomLoader from '../components/Loader/CustomLoader';
import { useQuery, useMutation, gql, useApolloClient } from '@apollo/client';
import {
  deletecsadmin,
  GetAllCustomerTypes,
  getalllocalstore,
} from '../apollo';
import DataTable from 'react-data-table-component';
import { customStyles } from '../utils/tableForManageCustomerType';
import useGlobalStyles from '../utils/globalStyles';
import TableHeader from '../components/TableHeader';
import {
  Container,
  Button,
  Modal,
  MenuItem,
  IconButton,
  Menu,
  ListItemIcon,
  Typography,
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react';

import AddCustomerType from '../components/AddCustomerType/AddCustomerType';
import UpdateCustomerType from '../components/UpdateCustomerType/UpdateCustomerType';

/* ---------------- GraphQL ---------------- */

const GET_ALL_CUSTOMER_TYPE = gql`${GetAllCustomerTypes}`;
const GET_LOCAL_STORE = gql`${getalllocalstore}`;
const DELETE_CSADMIN = gql`${deletecsadmin}`;

/* ---------------- View Modal ---------------- */

function ViewCustomerTypeCharges({ customerType, storeMap, onClose }) {
  if (!customerType) return null;

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '—';
    if (amount === '') return '-';
    const parsed = Number(amount);
    return Number.isNaN(parsed) ? '-' : parsed.toFixed(2);
  };

  const renderChargeTable = (title, slabs, color) => (
    <Box
      sx={{
        border: '1px solid #e5e7eb',
        borderRadius: '14px',
        overflow: 'hidden',
        bgcolor: '#ffffff',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: '#f8fafc',
          borderLeft: `4px solid ${color}`,
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 14 }}>
          {title}
        </Typography>
        <Typography sx={{ color: '#6b7280', fontSize: 12 }}>
          {slabs.length} slab{slabs.length === 1 ? '' : 's'}
        </Typography>
      </Box>

      {slabs.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            sx={{
              minWidth: 420,
              display: 'grid',
              gridTemplateColumns: '64px 1fr 1fr 1fr',
              bgcolor: '#637BFE',
            }}
          >
            {['#', 'Min Amount', 'Max Amount', 'Rate'].map((label) => (
              <Typography
                key={label}
                sx={{
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: 12,
                  px: 1.25,
                  py: 1,
                  textAlign: 'center',
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>

          {slabs.map((slab, index) => (
            <Box
              key={index}
              sx={{
                minWidth: 420,
                display: 'grid',
                gridTemplateColumns: '64px 1fr 1fr 1fr',
                borderBottom:
                  index === slabs.length - 1 ? 'none' : '1px solid #eef0f4',
                '&:nth-of-type(even)': {
                  bgcolor: '#fbfcff',
                },
              }}
            >
              <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 13, p: 1, textAlign: 'center' }}>
                {index + 1}
              </Typography>
              <Typography sx={{ color: '#111827', fontSize: 13, p: 1, textAlign: 'center' }}>
                {formatAmount(slab.minAmount)}
              </Typography>
              <Typography sx={{ color: '#111827', fontSize: 13, p: 1, textAlign: 'center' }}>
                {formatAmount(slab.maxAmount)}
              </Typography>
              <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 13, p: 1, textAlign: 'center' }}>
                {formatAmount(slab.rate)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography sx={{ color: '#6b7280', fontSize: 13, p: 2 }}>
          No {title.toLowerCase()} configured
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '18px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#637BFE',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: 18,
          py: 1.5,
          pr: 6,
          position: 'relative',
        }}
      >
        Store Wise Charges - {customerType.name}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#ffffff',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3, bgcolor: '#f9fafb' }}>
        {!customerType.storeCharges || customerType.storeCharges.length === 0 ? (
          <Typography sx={{ color: '#6b7280', textAlign: 'center', py: 5 }}>
            No charges configured
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {customerType.storeCharges.map((charge, storeIdx) => {
              const storeName = storeMap[charge.localStoreId] || 'Unknown Store';
              const handlingSlabs = charge.handling || [];
              const deliverySlabs = charge.delivery || [];

              return (
                <Box
                  key={storeIdx}
                  sx={{
                    border: '1px solid #dfe3eb',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    bgcolor: '#ffffff',
                    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderBottom: '1px solid #e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 15 }}>
                        {storeName}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: '#4b5563', fontSize: 12 }}>
                      {handlingSlabs.length + deliverySlabs.length} total slabs
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        {renderChargeTable('Handling Charges', handlingSlabs, '#1976d2')}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {renderChargeTable('Delivery Charges', deliverySlabs, '#2e7d32')}
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
 

/* ---------------- Main Component ---------------- */

function ManageCustomerType(props) {
  const { t } = props;
  const client = useApolloClient();
  const globalClasses = useGlobalStyles();

  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selected, setSelected] = useState(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');

  const userType = JSON.parse(localStorage.getItem('user-native'));

  const { data, loading, refetch } = useQuery(GET_ALL_CUSTOMER_TYPE, {
    variables: { storeId: userType.storeId },
    onError() {
      localStorage.clear();
      client.clearStore();
      props.history.push('/auth/login');
    },
  });

  const { data: storeData } = useQuery(GET_LOCAL_STORE);

  const storeMap = {};
  if (storeData && storeData.getAllLocalStores) {
    storeData.getAllLocalStores.forEach((s) => {
      storeMap[s._id] = s.name;
    });
  }

  const [mutate] = useMutation(DELETE_CSADMIN, {
    refetchQueries: [{ query: GET_ALL_CUSTOMER_TYPE }],
    onCompleted() {
      setAlertMessage('Deleted successfully');
      setAlertSeverity('success');
      setAlertOpen(true);
    },
    onError() {
      setAlertMessage('Something went wrong');
      setAlertSeverity('error');
      setAlertOpen(true);
    },
  });

  function ActionButtons({ row }) {
    const [anchorEl, setAnchorEl] = useState(null);

    function closeMenu() {
      setAnchorEl(null);
    }

    return (
  <>
    <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
      <MoreVertIcon fontSize="small" />
    </IconButton>

    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
      {/* VIEW — BLUE */}
      <MenuItem
        onClick={() => {
          setSelected(row);
          setViewModal(true);
          closeMenu();
        }}
        sx={{
          color: '#1976d2',
          '& .MuiListItemIcon-root': {
            color: '#1976d2',
          },
          '&:hover': {
            backgroundColor: '#e3f2fd',
          },
        }}
      >
        <ListItemIcon>
          <VisibilityIcon fontSize="small" />
        </ListItemIcon>
        {t('View')}
      </MenuItem>

      {/* UPDATE — GREEN */}
      <MenuItem
        onClick={() => {
          setSelected(row);
          setEditModal(true);
          closeMenu();
        }}
        sx={{
          color: '#2e7d32',
          '& .MuiListItemIcon-root': {
            color: '#2e7d32',
          },
          '&:hover': {
            backgroundColor: '#e8f5e9',
          },
        }}
      >
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        {t('Update Charges')}
      </MenuItem>

      {/* DELETE — RED */}
      <MenuItem
      disabled
        onClick={() => {
          mutate({ variables: { adminId: row._id } });
          closeMenu();
        }}
        sx={{
          color: '#d32f2f',
          '& .MuiListItemIcon-root': {
            color: '#d32f2f',
          },
          '&:hover': {
            backgroundColor: '#fdecea',
          },
        }}
      >
        <ListItemIcon>
          <DeleteIcon fontSize="small" />
        </ListItemIcon>
        {t('Delete')}
      </MenuItem>
    </Menu>
  </>
);

  }

  const columns = [
    { name: 'Sl No.', cell: (_, i) => i + 1, width: '80px' },
    { name: 'Name', selector: (row) => row.name, sortable: true },
    { name: 'Description', selector: (row) => row.description },
    { name: 'Default', selector: (row) => (row.default ? 'Yes' : 'No') },
    {
      name: 'Action',
      cell: (row) => <ActionButtons row={row} />,
      ignoreRowClick: true,
      button: true,
    },
  ];

  return (
    <>
      <Header />

      <Container classNname={globalClasses.flex} fluid>
        <Box textAlign="right" mb={3}>
          <Button
            className={globalClasses.dashbordBtn}
            onClick={() => setAddModal(true)}
          >
            <Icon icon="ic:twotone-plus" /> {t('Add New Customer Type')}
          </Button>
        </Box>

        {loading ? (
          <CustomLoader />
        ) : (
          <DataTable
            title={<TableHeader title={t('Manage Customer Type')} />}
            columns={columns}
            data={data ? data.getAllCustomerTypes : []}
            pagination
            customStyles={customStyles}
          />
        )}

        {addModal ? (
          <Modal open={true} onClose={() => setAddModal(false)}>
            <AddCustomerType onClose={() => setAddModal(false)} refetch={refetch} />
          </Modal>
        ) : null}

        {editModal ? (
          <Modal open={true} onClose={() => setEditModal(false)}>
            <UpdateCustomerType
              vendor={selected}
              onClose={() => setEditModal(false)}
              refetch={refetch}
            />
          </Modal>
        ) : null}

        {viewModal ? (
          <ViewCustomerTypeCharges
            customerType={selected}
            storeMap={storeMap}
            onClose={() => setViewModal(false)}
          />
        ) : null}

        <Snackbar open={alertOpen} autoHideDuration={6000} onClose={() => setAlertOpen(false)}>
          <Alert severity={alertSeverity}>{alertMessage}</Alert>
        </Snackbar>
      </Container>
    </>
  );
}

export default withTranslation()(ManageCustomerType);
